<template>
  <div>
    <div class="login-header">
      <h2>{{ activeTerms ? activeTerms.title : t('termsTitle') }}</h2>
    </div>

    <div class="terms-meta-row">
      <div class="terms-meta">
        <template v-if="termsStatus && termsStatus.currentVersion">
          {{ activeTerms ? activeTerms.versionLabel : t('termsVersionLabel') }}:
          <strong>{{ termsStatus.currentVersion }}</strong>
        </template>
      </div>
      <div class="lang-toggle" v-if="availableLangs.length > 1">
        <button
          v-for="lang in availableLangs"
          :key="lang"
          type="button"
          class="lang-btn"
          :class="{ active: termsLang === lang }"
          @click="termsLang = lang"
        >{{ lang.toUpperCase() }}</button>
      </div>
    </div>

    <div class="terms-body" v-if="activeTerms">
      <p v-for="(para, i) in paragraphs" :key="i">{{ para }}</p>
    </div>
    <div v-else class="error-message">
      {{ errorMessage || t('termsLoadFailed') }}
    </div>

    <label class="accept-row">
      <input type="checkbox" v-model="accepted" :disabled="busy || working" />
      <span>{{ activeTerms ? activeTerms.acceptCheckbox : t('termsAcceptCheckbox') }}</span>
    </label>

    <div v-if="localError" class="error-message">{{ localError }}</div>

    <div class="action-row">
      <button class="secondary-btn" @click="$emit('cancel')">
        {{ t('cancel') }}
      </button>
      <button
        :disabled="!accepted || busy || working"
        @click="onAccept"
      >{{ t('termsAccept') }}</button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'
import LoginApiService from '../services/LoginApiService.js'

function detectInitialLang(languages) {
  try {
    const raw = (navigator.language || navigator.userLanguage || '').toLowerCase()
    const short = raw.substring(0, 2)
    if (languages && languages[short]) return short
  } catch (e) { /* ignore */ }
  if (languages && languages.en) return 'en'
  if (languages) return Object.keys(languages)[0] || 'en'
  return 'en'
}

export default defineComponent({
  name: 'TermsView',

  props: {
    // { currentVersion, languages:{de:{title,body,versionLabel,acceptCheckbox,...},en:{...}},
    //   acceptedVersion, requiresAcceptance }
    termsStatus: { type: Object, default: null },
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' }
  },

  emits: ['accepted', 'cancel'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      accepted: false,
      working: false,
      localError: '',
      termsLang: detectInitialLang(this.termsStatus && this.termsStatus.languages)
    }
  },

  computed: {
    availableLangs() {
      return this.termsStatus && this.termsStatus.languages
        ? Object.keys(this.termsStatus.languages)
        : []
    },
    activeTerms() {
      if (!this.termsStatus || !this.termsStatus.languages) return null
      return this.termsStatus.languages[this.termsLang]
        || this.termsStatus.languages.en
        || this.termsStatus.languages[Object.keys(this.termsStatus.languages)[0]]
        || null
    },
    paragraphs() {
      const body = this.activeTerms && this.activeTerms.body
      if (!body) return []
      return String(body).split(/\n\n+/).map(s => s.trim()).filter(Boolean)
    }
  },

  methods: {
    async onAccept() {
      if (!this.accepted || !this.termsStatus || !this.termsStatus.currentVersion) return
      this.localError = ''
      this.working = true
      try {
        await LoginApiService.acceptTerms(this.termsStatus.currentVersion)
        this.$emit('accepted')
      } catch (e) {
        this.localError = (e && e.message) || this.t('errorGeneric')
      } finally {
        this.working = false
      }
    }
  }
})
</script>

<style scoped>
.login-header { margin-bottom: 12px; }
.login-header h2 { margin: 0; font-size: 1.25rem; }

.terms-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
}
.lang-toggle { display: inline-flex; gap: 4px; }
.lang-btn {
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}
.lang-btn.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.terms-body {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px 14px;
  background: #fafbfc;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
}
.terms-body p { margin: 0 0 10px 0; white-space: pre-wrap; }
.terms-body p:last-child { margin-bottom: 0; }

.accept-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  font-size: 14px;
  color: #1f2937;
}
.accept-row input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; }

.action-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
button {
  padding: 10px 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }
.secondary-btn { background: #f0f2f5; color: #1a1a2e; }
.secondary-btn:hover { background: #e4e8ee; }

.error-message {
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  padding: 10px 12px; border-radius: 6px; font-size: 13px; margin-top: 8px;
}
</style>
